'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { helpRegistry } from './help-registry';
import type { UserContext, UserContextProvider } from './user-context';

export interface DashboardSessionSummary {
  userId?: string;
  userEmail?: string;
  userName?: string;
  userType?: string;
  accountId?: string | null;
  accountName?: string | null;
  accountPlan?: string | null;
  creditsBalance?: number | null;
  featureFlags?: Record<string, boolean>;
}

/**
 * Builds a `UserContextProvider` whose return value is recomputed lazily on
 * every call. The provider is stable across renders, so we can safely pass
 * it to `ContextAwareRuntime` at construction time and the widget will
 * always see the latest route / session data.
 *
 * We track the last N distinct routes visited to populate `recentActions`.
 * Nothing fancy — a ring buffer in a ref. Helps the LLM understand "the
 * user was just on /credits and now asks about upload".
 */
export function useUserContextProvider(
  session: DashboardSessionSummary | null,
): UserContextProvider {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const recentRef = useRef<string[]>([]);
  const latestRef = useRef<UserContext | null>(null);

  useEffect(() => {
    if (!pathname) return;
    const buf = recentRef.current;
    const entry = `visited ${pathname}`;
    if (buf[0] !== entry) {
      buf.unshift(entry);
      if (buf.length > 5) buf.length = 5;
    }
    latestRef.current = buildContext({
      pathname,
      searchParams,
      session,
      recentActions: [...buf],
    });
  }, [pathname, searchParams, session]);

  // The provider is stable across renders. It returns whatever the most
  // recent effect populated into the ref, merged with the live help-hint
  // registry at call time. Reading the registry synchronously here means
  // hints that flipped in and out between turns are reflected accurately
  // without triggering a widget re-render.
  return () => {
    const base = latestRef.current;
    if (!base) return null;
    const hints = helpRegistry.snapshot();
    if (hints.length === 0) return base;
    return {
      ...base,
      visibleUi: hints.map((h) => ({
        id: h.id,
        label: h.label,
        description: h.description,
      })),
    };
  };
}

function buildContext(args: {
  pathname: string;
  searchParams: URLSearchParams | null;
  session: DashboardSessionSummary | null;
  recentActions: string[];
}): UserContext {
  const routeParams: Record<string, string> = {};
  if (args.searchParams) {
    for (const [k, v] of args.searchParams.entries()) {
      if (k.length <= 32 && v.length <= 80) routeParams[k] = v;
    }
  }

  const ctx: UserContext = {
    route: args.pathname,
    recentActions: args.recentActions,
  };
  if (Object.keys(routeParams).length > 0) ctx.routeParams = routeParams;
  if (args.session?.userType) ctx.userRole = args.session.userType;
  if (args.session?.accountPlan) ctx.accountPlan = args.session.accountPlan;
  if (args.session?.featureFlags) ctx.featureFlags = args.session.featureFlags;

  if (args.session?.creditsBalance != null) {
    ctx.hints = { creditsBalance: args.session.creditsBalance };
  }

  return ctx;
}
